// "Company access readiness" (GET rbac/entity-scope/readiness): everyone, apart
// from owners, who holds payroll or compensation permissions — and whether they
// still reach a company once company scoping is on for payroll.
//
// Four states, kept distinct: loading, error (shown as "readiness unknown" —
// never as ready), empty (nobody but owners holds payroll access), and data.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getEntityScopeReadiness,
  type EntityScopeReadiness,
  type ReadinessPerson,
  type ReadinessStatus,
} from '@/services/rbacService';
import { personLabel } from './lockoutConfirm';

type Tone = 'success' | 'warning' | 'danger';

/** Status chip wording: a forecast before activation, the present after it. */
export function statusLabel(status: ReadinessStatus, active: boolean): { text: string; tone: Tone } {
  if (status === 'ok') return { text: active ? 'OK' : 'Ready', tone: 'success' };
  if (status === 'partial') {
    return { text: active ? 'Missing some access' : 'Will lose some access', tone: 'warning' };
  }
  return { text: active ? 'Can’t see payroll or accounting' : 'Will lose payroll or accounting access', tone: 'danger' };
}

/** The panel's one-line verdict. Pure. */
export function readinessHeadline(d: EntityScopeReadiness): { text: string; tone: Tone } {
  const active = d.tenant.hasScopedAssignments;
  const n = d.summary.willLoseAccess;
  const people = (k: number) => `${k} ${k === 1 ? 'person' : 'people'}`;
  if (!d.ready) {
    return active
      ? { text: `Company scoping is on — ${people(n)} can’t see payroll or accounting right now.`, tone: 'danger' }
      : {
          text: `Not ready — ${people(n)} would stop seeing payroll or accounting when company scoping turns on.`,
          tone: 'danger',
        };
  }
  if (d.summary.partial > 0) {
    return {
      text: `${active ? 'Company scoping is on.' : 'Ready to turn on.'} ${people(d.summary.partial)} would keep only part of their access.`,
      tone: 'warning',
    };
  }
  return active
    ? { text: 'Company scoping is on. Everyone with payroll or accounting access reaches their companies.', tone: 'success' }
    : { text: 'Ready — everyone with payroll or accounting access has companies assigned.', tone: 'success' };
}

/**
 * What the person loses, in plain words — "Will lose: payroll runs, payslips,
 * filings; salaries" (or "Can’t see: …" once scoping is on). Falls back to the
 * permission keys for an older server that sends no surfaces. Pure.
 */
export function lossLine(person: Pick<ReadinessPerson, 'losesPermissions' | 'losesSurfaces'>, active: boolean): string {
  const surfaces = person.losesSurfaces ?? [];
  if (surfaces.length > 0) return `${active ? 'Can’t see' : 'Will lose'}: ${surfaces.join('; ')}`;
  return `No company for: ${person.losesPermissions.join(', ')}`;
}

const TONE_BOX: Record<Tone, string> = {
  success: 'bg-success-soft text-success-strong',
  warning: 'bg-warning-soft text-warning-strong',
  danger: 'bg-danger-soft text-danger-strong',
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; variant: 'error' | 'denied'; message: string }
  | { kind: 'data'; data: EntityScopeReadiness };

interface Props {
  /** Bump to reload (after an assignment changes). */
  reloadKey?: number;
  onOpenAccess: (userId: string, name: string) => void;
}

export const EntityScopeReadinessPanel: React.FC<Props> = ({ reloadKey = 0, onOpenAccess }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'data', data: await getEntityScopeReadiness() });
    } catch (e) {
      setState({
        kind: 'error',
        variant: errorVariant(e),
        message: getApiErrorMessage(e, 'Could not check company access readiness.'),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  return (
    <Card data-testid="entity-scope-readiness">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-h3 text-foreground">Company access readiness</h3>
            <p className="text-sm text-muted-foreground">
              Once any role is limited to specific companies, payroll shows each person only the
              companies they are given. Check that everyone with payroll or accounting access has theirs first.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={state.kind === 'loading'}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Re-check
          </Button>
        </div>

        {state.kind === 'loading' && <DataState compact variant="loading" message="Checking who has payroll or accounting access…" />}

        {state.kind === 'error' && (
          <DataState
            variant={state.variant}
            title="Readiness unknown"
            message={state.message}
            onRetry={() => void load()}
          />
        )}

        {state.kind === 'data' && state.data.people.length === 0 && (
          <DataState
            variant="empty"
            title="No one to check"
            message={`Apart from owners${state.data.summary.ownersExcluded ? ` (${state.data.summary.ownersExcluded})` : ''}, nobody holds payroll or compensation permissions.`}
          />
        )}

        {state.kind === 'data' && state.data.people.length > 0 && (
          <ReadinessBody data={state.data} onOpenAccess={onOpenAccess} />
        )}
      </CardContent>
    </Card>
  );
};

const ReadinessBody: React.FC<{
  data: EntityScopeReadiness;
  onOpenAccess: (userId: string, name: string) => void;
}> = ({ data, onOpenAccess }) => {
  const headline = readinessHeadline(data);
  const active = data.tenant.hasScopedAssignments;
  return (
    <div className="space-y-3">
      <div className={`rounded-[10px] px-3 py-2 text-sm font-medium ${TONE_BOX[headline.tone]}`} role="status">
        {headline.text}
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant={active ? 'info' : 'neutral'}>
          {active ? 'Company scoping: on' : 'Company scoping: not on yet'}
        </Badge>
        <Badge variant="neutral" title={data.entities.error ?? undefined}>
          {data.entities.count === null ? 'Companies: unknown' : `${data.entities.count} ${data.entities.count === 1 ? 'company' : 'companies'}`}
        </Badge>
        <Badge variant="neutral">{data.summary.total} with payroll or accounting access</Badge>
        {data.summary.ownersExcluded > 0 && (
          <Badge variant="neutral">{data.summary.ownersExcluded} owner{data.summary.ownersExcluded === 1 ? '' : 's'} not affected</Badge>
        )}
      </div>
      <ul className="divide-y divide-border max-h-[40vh] overflow-y-auto">
        {data.people.map((p) => (
          <ReadinessRow key={p.userId} person={p} active={active} onOpenAccess={onOpenAccess} />
        ))}
      </ul>
    </div>
  );
};

const ReadinessRow: React.FC<{
  person: ReadinessPerson;
  active: boolean;
  onOpenAccess: (userId: string, name: string) => void;
}> = ({ person, active, onOpenAccess }) => {
  const s = statusLabel(person.status, active);
  const name = personLabel(person);
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`readiness-${person.userId}`}>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground truncate">{name}</span>
        {person.email && person.name && (
          <span className="block text-xs text-muted-foreground truncate">{person.email}</span>
        )}
        {person.losesPermissions.length > 0 && (
          <span
            className="block text-xs text-muted-foreground"
            title={`No company for: ${person.losesPermissions.join(', ')}`}
          >
            {lossLine(person, active)}
          </span>
        )}
      </span>
      <Badge variant={s.tone}>{s.text}</Badge>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onOpenAccess(person.userId, name)}
        aria-label={`Effective access for ${name}`}
      >
        <ShieldCheck className="w-4 h-4 mr-1" />
        Effective access
      </Button>
    </li>
  );
};

export default EntityScopeReadinessPanel;
